import { useCallback, useEffect, useMemo, useState, ReactNode } from 'react';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import MultipleVsPeersTable from './components/MultipleVsPeersTable';
import MultipleVsPeersChart from './components/MultipleVsPeersChart';
import { COMPANY_VALUATION_MULTIPLE_VS_PEERS } from '@/constants/feedbackSections';
import { useSelector, shallowEqual } from 'react-redux';
import selector, { PreferredMultiple, UserPeerSelection } from '../../selector';
import { Toggle, Text, Alert, AlertAction } from '@simplywallst/ui-core';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';
import {
  MultipleVsPeersWrapper,
  ToggleWrapper,
  ToggleLabel,
  DropdownWrapper,
  PillButton,
} from './styled';
import { useQuery } from '@tanstack/react-query';
import { getPeersQueryKey, queryPeersFn, CustomPeer } from '../../query';
import type { CurrencyISO } from '@/constants/currencies';
import { getMultipleFullName, mapCompanyToPeer } from '../../utils';
import If from '@/components/If';
import Tooltip from '@/components/Tooltip';
import ReportSubSection from '@components/ReportSubSection';
import MultipleDropdown from '../MultipleDropdown';
import CustomPeersModal from './components/MultipleVsPeersChart/components/CustomPeersModal';
import { isEqual } from 'lodash';
import { useTracking } from 'react-tracking';
import { PEERS_LIMIT } from '@/pages/CompanyReport/constants';
import {
  useDeleteUserCompanyPeers,
  useUpsertUserCompanyPeers,
  useUserCompanyPeers,
} from '@/hooks/useUserCompanyPeers/useUserCompanyPeers';
import SavedPeersDropdown from '@/pages/CompanyReport/components/Valuation/components/SavedPeersDropdown';
import PeersHelpContent from '@/pages/CompanyReport/components/Valuation/components/MultipleVsPeers/components/PeersHelpContent';
import { AlertWrapper } from '@components/ReportSubSection/styled';
import { useVisibleSectionsContext } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import ScreenerLinkButton from '@/pages/CompanyReport/components/ScreenerLinkButton';
import { GridViewOptions } from '@/pages/CompanyReport/constants/constants';
import { VALUATION_RATIO_VS_PEERS } from '@/constants/anchors';
import { getPeersRatioAverage } from './utils';
export interface Peer {
  companyId: string;
  name: string;
  tickerSymbol: string;
  uniqueSymbol: string;
  canonicalURL: string;
  listingCurrencyISO: CurrencyISO;
  ratio: number;
  ratioFormatted: string;
  earningsGrowth: number;
  earningsGrowthFormatted: string;
  salesGrowth: number;
  salesGrowthFormatted: string;
  marketCap: number;
  marketCapFormatted: string;
  marketCapUSD: number;
  marketCapUSDFormatted: string;
  preferredCurrentRatio?: number;
  currentRatio?: number;
  forwardRatio?: number;
}

interface Props {
  preferredMultiple: PreferredMultiple;
  statements?: ReactNode;
}

export const initCustomPeers: CustomPeer[] = [
  { id: '', name: '' },
  { id: '', name: '' },
  { id: '', name: '' },
  { id: '', name: '' },
];

const MultipleVsPeers = ({ preferredMultiple, statements }: Props) => {
  const { isPristine } = useVisibleSectionsContext();
  const { Track, trackEvent } = useTracking({ subject: 'MultipleVsPeers' });
  const [multiple, setMultiple] =
    useState<PreferredMultiple>(preferredMultiple);
  const [userPeerOption, setUserPeerOption] =
    useState<UserPeerSelection>('saved');
  const [toggleForward, setToggleForward] = useState(false);
  const {
    companyId,
    forwardPE,
    forwardPS,
    tickerSymbol,
    canonicalUrl,
    metricsData,
    uniqueSymbol,
  } = useSelector<Parameters<typeof selector>[0], ReturnType<typeof selector>>(
    selector,
    shallowEqual
  );

  const vsPeersDescription = `How does ${tickerSymbol}'s ${multiple} Ratio compare to its peers?`;
  const [showEditModal, setShowEditModal] = useState(false);
  const [userPeerCollectionId, setUserPeerCollectionId] = useState<
    string | null
  >(null);
  const [savedUserPeers, setSavedUserPeers] = useState<CustomPeer[]>([]);
  const [deletionWasFinished, setDeletionWasFinished] =
    useState<boolean>(false);
  const [loadDefaultPeers, setLoadDefaultPeers] = useState<boolean>(false);
  const [customPeers, setCustomPeers] = useState<CustomPeer[]>(initCustomPeers);
  const [isCustomPeersSelected, setIsCustomPeersSelected] = useState(false);
  const { data, status: peersStatus } = useQuery({
    queryKey: getPeersQueryKey(
      canonicalUrl,
      PEERS_LIMIT,
      isEqual(customPeers, initCustomPeers)
        ? []
        : [
            customPeers[0]?.id,
            customPeers[1]?.id,
            customPeers[2]?.id,
            customPeers[3]?.id,
          ]
    ),
    queryFn: queryPeersFn,
    staleTime: Infinity,
    enabled: Boolean(canonicalUrl),
    refetchOnMount: false,
  });

  const peers = useMemo((): Peer[] => {
    try {
      if (!data) return [];
      const { peers, ...companyData } = data.Company;
      const selectedCompany = mapCompanyToPeer(
        multiple,
        toggleForward,
        preferredMultiple
      )(companyData);
      const peersData: Peer[] = peers.map((peer) =>
        mapCompanyToPeer(
          multiple,
          toggleForward,
          preferredMultiple
        )(peer.company)
      );
      return peersData.concat(selectedCompany);
    } catch (error) {
      console.log(error);
      return [];
    }
  }, [data, multiple, preferredMultiple, toggleForward]);
  const noPeersData = peers.length === 1;

  const { average, averageFormatted } = getPeersRatioAverage(
    peers,
    data?.Company.uniqueSymbol
  );

  const getDefaultPeers = useCallback(() => {
    const defaultPeers: Peer[] = [70, 55, 28, 23, 17].map((value, i) => ({
      canonicalURL: '',
      companyId: i === 1 ? companyId : String(i),
      earningsGrowth: 0,
      earningsGrowthFormatted: '',
      listingCurrencyISO: 'USD',
      marketCap: 0,
      marketCapFormatted: 'n/a',
      marketCapUSD: 0,
      marketCapUSDFormatted: 'n/a',
      name: '',
      ratio: value,
      ratioFormatted: '',
      salesGrowth: 0,
      salesGrowthFormatted: '',
      tickerSymbol: '',
      uniqueSymbol: '',
    }));
    return defaultPeers;
  }, [companyId]);

  useEffect(() => {
    setToggleForward(false);
  }, [multiple]);

  //Initially set multiple selection to the company's preferred one
  useEffect(() => {
    setMultiple(preferredMultiple);
  }, [preferredMultiple]);

  const disableToggle =
    (multiple === 'PE' && forwardPE === 'n/a') ||
    (multiple === 'PS' && forwardPS === 'n/a');

  const savedPeers = useUserCompanyPeers(companyId);

  const forwardToggle = (
    <ToggleWrapper>
      <Toggle
        disabled={disableToggle}
        name="forward-multiple-toggle-peers"
        label={<ToggleLabel>Forward {multiple}</ToggleLabel>}
        value={toggleForward}
        onChange={() => {
          trackEvent({
            subject: 'valuation',
            action: `toggle_${!toggleForward ? 'on' : 'off'}`,
            modifier: `vs_peers_forward_${multiple}`,
          });
          setToggleForward((toggleForward) => !toggleForward);
        }}
      />
    </ToggleWrapper>
  );

  const { createUserPeers, creationStatus } =
    useUpsertUserCompanyPeers(companyId);

  const { deleteUserPeers, deletionStatus } =
    useDeleteUserCompanyPeers(companyId);

  useEffect(() => {
    if (savedPeers.status === 'success') {
      if (savedPeers.data.length > 0) {
        setUserPeerCollectionId(savedPeers.data[0].id);
        const customSavedPeers = savedPeers.data[0].peers.map((peer) => {
          return { id: peer.uniqueSymbol, name: '' };
        });
        if (
          !customPeers
            .map((item) => item.id)
            .every((i) =>
              customSavedPeers.map((item) => item.id).includes(i)
            ) &&
          loadDefaultPeers === false
        ) {
          setCustomPeers([...customSavedPeers]);
          setIsCustomPeersSelected(true);
          setUserPeerOption('saved');
        }
      }
    }
  }, [savedPeers, loadDefaultPeers, customPeers]);

  useEffect(() => {
    //holding the actual saved peers in a state
    if (
      !isEqual(customPeers, initCustomPeers) ||
      //initial state
      (savedUserPeers.length === 0 &&
        peersStatus === 'success' &&
        !loadDefaultPeers)
    ) {
      setSavedUserPeers(
        data?.customPeers ? data?.customPeers : initCustomPeers
      );
    }
  }, [customPeers, data, peersStatus, savedUserPeers, loadDefaultPeers]);

  useEffect(() => {
    if (
      deletionStatus === 'success' &&
      userPeerCollectionId &&
      !deletionWasFinished
    ) {
      setUserPeerCollectionId(null);
      setSavedUserPeers(
        data?.customPeers ? data?.customPeers : initCustomPeers
      );
      setDeletionWasFinished(true);
    }
  }, [deletionStatus, userPeerCollectionId, deletionWasFinished]);

  const handleSubmitCustomPeers = (selectedPeers: CustomPeer[]) => {
    if (!isCustomPeersSelected) {
      setCustomPeers([...initCustomPeers]);
      setShowEditModal(false);
      if (userPeerCollectionId) {
        setDeletionWasFinished(false);
        deleteUserPeers(userPeerCollectionId);
      }
    } else {
      setCustomPeers([...selectedPeers]);
      setShowEditModal(false);

      createUserPeers({
        userPeerCollection: {
          name: 'Custom peers for ' + uniqueSymbol,
          uniqueSymbol: uniqueSymbol,
          description: 'Saved Peers Description',
          peers: [...selectedPeers].map((peer) => {
            return { uniqueSymbol: peer.id };
          }),
        },
        collectionId: userPeerCollectionId,
      });
      setLoadDefaultPeers(false);
      setUserPeerOption('saved');

      trackEvent({
        action: 'submit',
        modifier: 'Edit Peers',
        uniqueSymbol: uniqueSymbol,
        customPeers: JSON.stringify(selectedPeers),
      });
    }
  };
  const handleCloseModal = () => {
    setShowEditModal(false);
    if (userPeerOption === 'edit' && loadDefaultPeers) {
      setUserPeerOption('default');
    } else {
      setUserPeerOption('saved');
    }
  };
  const handleResetAllPeers = (resetChart = false) => {
    if (resetChart) {
      setCustomPeers([...initCustomPeers]);
    }
    setLoadDefaultPeers(true);
  };
  const onDropDownSelection = (option: UserPeerSelection) => {
    if (option === 'edit') {
      setShowEditModal(true);
    }
    if (option === 'saved') {
      setLoadDefaultPeers(false);
      setIsCustomPeersSelected(true);
    }
    if (option === 'default') {
      handleResetAllPeers(true);
    }
    setUserPeerOption(option);
    trackEvent({
      action: 'select ' + option,
      modifier: 'Editing saved Peers ',
      uniqueSymbol: uniqueSymbol,
    });
  };

  return (
    <Track>
      <ReportSubSection
        titleElement={isPristine ? 'h2' : 'h3'}
        title={`${getMultipleFullName(multiple)} Ratio vs Peers`}
        description={vsPeersDescription}
        bodyRender={() => (
          <MultipleVsPeersWrapper>
            {creationStatus === 'error' && (
              <AlertWrapper>
                <Alert
                  styleType="warning"
                  actions={[<AlertAction key="ok">OK</AlertAction>]}
                >
                  Sorry we weren't able to save your peers, we are looking into
                  this asap!
                </Alert>
              </AlertWrapper>
            )}
            <DropdownWrapper>
              {(userPeerCollectionId === null ||
                savedPeers.status === 'error') && (
                <PillButton
                  styleType="passive"
                  alignIcon="left"
                  marginRight
                  onClick={() => {
                    setShowEditModal(true);
                    trackEvent({
                      action: 'click',
                      modifier: 'Edit Peers',
                      uniqueSymbol: data?.Company.uniqueSymbol,
                    });
                  }}
                  disabled={
                    !data?.customPeers ||
                    peersStatus !== 'success' ||
                    deletionStatus === 'pending'
                  }
                >
                  <EditSolid width="24" height="24" />
                  <Text typography="tiny">Edit Peers</Text>
                </PillButton>
              )}
              {userPeerCollectionId && (
                <SavedPeersDropdown
                  selectedOption={userPeerOption}
                  disabled={
                    !data?.customPeers ||
                    peersStatus !== 'success' ||
                    deletionStatus === 'pending'
                  }
                  onSelect={onDropDownSelection}
                  id={'vsPeers'}
                />
              )}
              {showEditModal && peersStatus === 'success' && (
                <CustomPeersModal
                  uniqueSymbol={data.Company.uniqueSymbol}
                  currentPeers={savedUserPeers}
                  canonicalUrl={canonicalUrl}
                  onClose={handleCloseModal}
                  onResetAllPeers={() => handleResetAllPeers(false)}
                  onSubmitCustomPeers={handleSubmitCustomPeers}
                  onUpdatePeers={(isCustomPeerSelected) =>
                    setIsCustomPeersSelected(isCustomPeerSelected)
                  }
                />
              )}
              <MultipleDropdown
                id="vsPeers"
                preferredMultiple={preferredMultiple}
                multiple={multiple}
                onSelect={setMultiple}
              />
            </DropdownWrapper>
            <ChartWrapper
              id="valuation-multiple-vs-peers"
              defaultModeText={
                noPeersData
                  ? 'Insufficient data to calculate this valuation analysis check'
                  : undefined
              }
              chart={
                noPeersData ? (
                  <MultipleVsPeersChart
                    companyId={companyId}
                    preferredMultiple={multiple}
                    peers={getDefaultPeers()}
                    peersAverage={38}
                    insufficientData={noPeersData}
                  />
                ) : (
                  <MultipleVsPeersChart
                    companyId={companyId}
                    preferredMultiple={multiple}
                    peers={peers}
                    peersAverage={average}
                    status={peersStatus}
                  />
                )
              }
              table={
                <MultipleVsPeersTable
                  companyId={companyId}
                  tickerSymbol={tickerSymbol}
                  preferredMultiple={multiple}
                  peers={peers}
                  averageFormatted={averageFormatted}
                  useForwardRatio={toggleForward}
                />
              }
              preRenderHeight={382}
              actionsMarginTop={16}
              feedbackLabel={COMPANY_VALUATION_MULTIPLE_VS_PEERS}
              help={<PeersHelpContent />}
              helpTitle="Preferred Ratio vs Peers"
              moreActions={
                <>
                  <If
                    conditions={{
                      withTooltip: multiple !== 'PB' && disableToggle,
                      withoutTooltip: multiple !== 'PB' && !disableToggle,
                    }}
                  >
                    {{
                      withTooltip: () => (
                        <Tooltip
                          openDelay={300}
                          light
                          title={
                            <span>
                              {`No forecast ${String(
                                metricsData[multiple].label
                              ).toLowerCase()} data to calculate forward ${multiple}`}
                            </span>
                          }
                        >
                          {forwardToggle}
                        </Tooltip>
                      ),
                      withoutTooltip: () => forwardToggle,
                    }}
                  </If>
                </>
              }
            />
          </MultipleVsPeersWrapper>
        )}
        statements={statements}
      />
      <ScreenerLinkButton
        ideaName="strong-past-earnings-growth-p-e-ratio-below-20"
        gridViewOptionId={GridViewOptions.GRID_VIEW_IN_CONTEXT_VALUATION}
        subSection={VALUATION_RATIO_VS_PEERS}
      />
    </Track>
  );
};
export default MultipleVsPeers;
